import React from "react";
import PageHeader from "../components/page-header";
import SvgIcons from "../components/svg-icons";

const CompanyWhyUs = () => {
  return (
    <div className="page-why-us">
      <PageHeader page="why-us" />
      <div className="container">
        <p className="text-center mb-5">
          TravelCarma helps travel companies modernize carefully, AI-enable
          existing workflows, manage and extend legacy systems, integrate and
          normalize supplier/API data, and build bespoke travel platforms when
          standard products cannot fit. Our advantage comes from 20+ years of
          building real travel technology: booking engines, B2B/B2C portals,
          Travel CRM, back-office systems, API aggregation layers, and
          enterprise travel infrastructure.
        </p>

        <div className="text-center">
          <div>
            <h2 className="mb-5">Our Travel Technology Depth</h2>
            <ul className="shadow-box list-unstyled row">
              <li className="col-lg-4 mb-3">
                <div>
                  {SvgIcons?.domain_expertise}
                  <h3>Travel Domain Expertise</h3>
                  <p className="text-secondary">
                    For over 20 years, TravelCarma has worked with travel and
                    hospitality companies across booking, distribution, CRM,
                    supplier connectivity, and back-office operations. That
                    domain depth helps us understand real operating complexity
                    before recommending AI, modernization, or custom
                    engineering.
                  </p>
                </div>
              </li>
              <li className="col-lg-4 mb-3">
                <div>
                  {SvgIcons?.supplier_flight}
                  <h3>Supplier Integration Expertise</h3>
                  <p className="text-secondary">
                    We bring deep experience in supplier/API integration and
                    normalization, with connectivity experience across 80+
                    travel suppliers. This helps clients unify supplier content,
                    manage booking workflows, and prepare travel data for
                    AI-assisted search, pricing, reporting, and automation.
                  </p>
                </div>
              </li>
              <li className="col-lg-4 mb-3">
                <div>
                  {SvgIcons.global_touch}
                  <h3>Enterprise Travel Experience</h3>
                  <p className="text-secondary">
                    With 150+ clients across 50+ countries, TravelCarma
                    understands multilingual, multicurrency, and region-specific
                    travel technology requirements. This global implementation
                    experience supports enterprise-grade AI enablement and
                    platform modernization.
                  </p>
                </div>
              </li>
            </ul>
          </div>

          <div className="mt-4">
            <h2 className="mb-5">Integration-Ready Architecture</h2>
            <ul className="shadow-box list-unstyled row">
              <li className="col-lg-4 mb-3">
                <div>
                  {SvgIcons?.channel_management}
                  <h3>Supplier and Channel Orchestration</h3>
                  <p className="text-secondary">
                    Our middleware, API aggregation, and channel management
                    experience helps travel companies connect suppliers,
                    distributors, agents, and customer-facing systems into
                    usable travel technology layers.
                  </p>
                </div>
              </li>
              <li className="col-lg-4 mb-3">
                <div>
                  {SvgIcons?.flexibility_scale}
                  <h3>Scalable Travel Platform Engineering</h3>
                  <p className="text-secondary">
                    Our platform-building experience supports phased
                    modernization, dedicated travel technology engineering
                    execution, bespoke platform builds, and integration-ready
                    architecture for travel companies that need to scale without
                    disrupting live operations.
                  </p>
                </div>
              </li>
              <li className="col-lg-4 mb-3">
                <div>
                  {SvgIcons.integrates_technology}
                  <h3>Works With Existing Systems</h3>
                  <p className="text-secondary">
                    We do not force rip-and-replace. We help preserve and
                    extend accounting, customer, CRM, supplier, booking, and
                    back-office systems while adding practical AI and custom
                    modules where they create value.
                  </p>
                </div>
              </li>
            </ul>
          </div>

          <div className="mt-4">
            <h2 className="mb-5">Practical AI-Enabled Travel Workflows</h2>
            <ul className="shadow-box list-unstyled row">
              <li className="col-lg-4 mb-3">
                <div>
                  {SvgIcons?.mobile_responsive}
                  <h3>AI-Enabled Portals and Experiences</h3>
                  <p className="text-secondary">
                    TravelCarma helps add AI-assisted search, recommendations,
                    service workflows, and personalized experiences to B2B/B2C
                    travel portals and customer-facing systems.
                  </p>
                </div>
              </li>
              <li className="col-lg-4 mb-3">
                <div>
                  {SvgIcons?.dynamic_packaging}
                  <h3>Itinerary and Quote Automation</h3>
                  <p className="text-secondary">
                    Our experience with dynamic packaging, itinerary builders,
                    and quotation workflows gives us the foundation to design
                    AI-assisted quote creation, itinerary planning, and
                    operational automation.
                  </p>
                </div>
              </li>
              <li className="col-lg-4 mb-3">
                <div>
                  {SvgIcons.payment_flexibility}
                  <h3>Flexible Booking and Payment Workflows</h3>
                  <p className="text-secondary">
                    Travel businesses often need custom booking logic, payment
                    rules, approval flows, and commercial controls. We design
                    workflows around the operating model rather than forcing
                    product fit.
                  </p>
                </div>
              </li>
            </ul>
          </div>

          <div className="mt-4">
            <h2 className="mb-5">Managed Operations and Productivity</h2>
            <ul className="shadow-box list-unstyled row">
              <li className="col-lg-4 mb-3">
                <div>
                  {SvgIcons?.time_cost_savings}
                  <h3>Operational Automation</h3>
                  <p className="text-secondary">
                    We help reduce manual work across reservations, support,
                    administration, reporting, supplier coordination, and
                    back-office processes using workflow automation and
                    practical AI enablement.
                  </p>
                </div>
              </li>
              <li className="col-lg-4 mb-3">
                <div>
                  {SvgIcons?.package_quote_creation}
                  <h3>Fast Package and Quote Creation</h3>
                  <p className="text-secondary">
                    Our trip planner and quotation experience helps travel
                    companies speed up package creation, itinerary preparation,
                    and customer response cycles with automation and AI
                    assistance.
                  </p>
                </div>
              </li>
              <li className="col-lg-4 mb-3">
                <div>
                  {SvgIcons.resource_optimization}
                  <h3>Managed System Extension</h3>
                  <p className="text-secondary">
                    We help maintain, support, and extend working travel systems
                    over time so clients can modernize in controlled phases
                    while keeping business operations stable.
                  </p>
                </div>
              </li>
            </ul>
          </div>

          <div className="mt-4">
            <h2 className="mb-5">Commercial Logic and Revenue Control</h2>
            <ul className="shadow-box list-unstyled row">
              <li className="col-lg-4 mb-3">
                <div>
                  {SvgIcons?.customer_class_wise_pricing}
                  <h3>Customer-Specific Pricing Logic</h3>
                  <p className="text-secondary">
                    TravelCarma understands complex customer classes, pricing
                    rules, markups, discounts, and revenue controls across
                    travel agency, OTA, wholesaler, and enterprise workflows.
                  </p>
                </div>
              </li>
              <li className="col-lg-4 mb-3">
                <div>
                  {SvgIcons?.agent_class_wise_pricing}
                  <h3>Agent and Partner Pricing</h3>
                  <p className="text-secondary">
                    Our B2B and B2B2C platform experience supports agent
                    classes, partner markups, commission structures, reseller
                    networks, and multi-channel distribution models.
                  </p>
                </div>
              </li>
              <li className="col-lg-4 mb-3">
                <div>
                  {SvgIcons.superior_management}
                  <h3>Supplier Feed Optimization</h3>
                  <p className="text-secondary">
                    We help normalize supplier feeds, manage business rules, and
                    design switching logic that supports better availability,
                    pricing, reliability, and automated operations.
                  </p>
                </div>
              </li>
            </ul>
          </div>

          <div className="mt-4">
            <h2 className="mb-5">Back-Office and Reconciliation Experience</h2>
            <ul className="shadow-box list-unstyled row">
              <li className="col-lg-4 mb-3">
                <div>
                  {SvgIcons?.supplier_management}
                  <h3>Supplier and Ledger Workflows</h3>
                  <p className="text-secondary">
                    Our back-office experience covers supplier accounting,
                    ledger workflows, reconciliation, purchase records, and
                    operational controls required by real travel businesses.
                  </p>
                </div>
              </li>
              <li className="col-lg-4 mb-3">
                <div>
                  {SvgIcons?.customer_management}
                  <h3>Customer and Booking Administration</h3>
                  <p className="text-secondary">
                    We understand invoices, refunds, cancellations, payments,
                    bookings, customer records, and service workflows - the
                    operational layer that AI and automation must work with
                    correctly.
                  </p>
                </div>
              </li>
              <li className="col-lg-4 mb-3">
                <div>
                  {SvgIcons.peace_of_mind}
                  <h3>Reliable Back-Office Systems</h3>
                  <p className="text-secondary">
                    Reporting, reconciliation, supplier management, customer
                    management, and operational reliability remain critical. We
                    help improve these systems without disrupting the business
                    that depends on them.
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyWhyUs;
